import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Map as MapIcon, Search } from "lucide-react";
import {
  Body,
  Caption,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VButton,
  VDataTable,
  VInput,
  VKpiCard,
  VSelect,
  type VDataTableColumn,
} from "@/components/ui";
import { useAssets } from "@/domains/assets/api/queries";
import type { Asset, AssetStatus, AssetType } from "@/domains/assets/api/types";
import { AssetStatusBadge } from "@/domains/assets/components/assetStatusBadge";
import {
  AssetTypeIcon,
  assetTypeLabel,
} from "@/domains/assets/components/assetTypeLabel";

const TYPE_OPTIONS: ReadonlyArray<{ value: AssetType | "all"; label: string }> = [
  { value: "all", label: "Alle types" },
  { value: "camera", label: "Mobiele camera" },
  { value: "mobileTree", label: "Mobile tree" },
  { value: "camcar", label: "Camcar" },
  { value: "batteryPack", label: "Batterij pack" },
  { value: "trailer", label: "Trailer" },
];

const STATUS_OPTIONS: ReadonlyArray<{ value: AssetStatus | "all"; label: string }> = [
  { value: "all", label: "Alle statussen" },
  { value: "available", label: "Beschikbaar" },
  { value: "inUse", label: "In gebruik" },
  { value: "maintenance", label: "Onderhoud" },
  { value: "outOfService", label: "Buiten werking" },
];

function parseTypeFilter(value: string): AssetType | "all" {
  return TYPE_OPTIONS.find((o) => o.value === value)?.value ?? "all";
}

function parseStatusFilter(value: string): AssetStatus | "all" {
  return STATUS_OPTIONS.find((o) => o.value === value)?.value ?? "all";
}

export function AssetListPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data, isPending, isError } = useAssets();
  const [search, setSearch] = useState("");
  const [type, setType] = useState<AssetType | "all">("all");
  const [status, setStatus] = useState<AssetStatus | "all">("all");

  const filtered = useMemo<ReadonlyArray<Asset>>(() => {
    const list = data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((a) => {
      if (type !== "all" && a.type !== type) return false;
      if (status !== "all" && a.status !== status) return false;
      if (!q) return true;
      return (
        a.number.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q) ||
        a.serialNumber.toLowerCase().includes(q)
      );
    });
  }, [data, search, type, status]);

  const stats = useMemo(() => {
    const list = data ?? [];
    return {
      total: list.length,
      available: list.filter((a) => a.status === "available").length,
      inUse: list.filter((a) => a.status === "inUse").length,
      outOfService: list.filter((a) => a.status === "outOfService").length,
    };
  }, [data]);

  const columns: ReadonlyArray<VDataTableColumn<Asset>> = [
    {
      key: "number",
      header: "Nummer",
      cell: (a) => (
        <span className="flex items-center gap-2">
          <AssetTypeIcon type={a.type} className="size-4 text-muted-foreground" />
          <span className="font-semibold">{a.number}</span>
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      cell: (a) => <Caption tone="default">{assetTypeLabel(a.type)}</Caption>,
    },
    {
      key: "name",
      header: "Naam",
      cell: (a) => (
        <div>
          <Body weight="medium">{a.name}</Body>
          <Caption tone="muted">{a.serialNumber}</Caption>
        </div>
      ),
    },
    {
      key: "location",
      header: "Locatie",
      cell: (a) =>
        a.currentLocation ? (
          <div>
            <Body>{a.currentLocation.address}</Body>
            <Caption tone="muted">{a.currentLocation.district}</Caption>
          </div>
        ) : (
          <Caption tone="muted">Depot</Caption>
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (a) => <AssetStatusBadge status={a.status} />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Assets"
        subtitle="Volledige inventaris van mobiele camera's, mobile trees, camcars, batterij packs en trailers."
        actions={
          <VButton variant="outline" asChild>
            <Link to="/assets/map">
              <MapIcon className="size-4" aria-hidden />
              Kaart-view
            </Link>
          </VButton>
        }
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <VKpiCard label="Totaal" value={stats.total} />
        <VKpiCard label="Beschikbaar" value={stats.available} helper="Direct inzetbaar" />
        <VKpiCard label="In gebruik" value={stats.inUse} helper="Op locatie" />
        <VKpiCard
          label="Buiten werking"
          value={stats.outOfService}
          helper="Wacht op herstelling"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full min-w-[240px] max-w-sm flex-1 sm:w-auto">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <VInput
            placeholder="Zoek op nummer, naam of serienummer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="w-[200px]">
          <VSelect
            value={type}
            onChange={(e) => setType(parseTypeFilter(e.target.value))}
            aria-label="Filter op type"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </VSelect>
        </div>
        <div className="w-[200px]">
          <VSelect
            value={status}
            onChange={(e) => setStatus(parseStatusFilter(e.target.value))}
            aria-label="Filter op status"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </VSelect>
        </div>
      </div>

      {isPending ? (
        <LoadingState label="Assets laden…" />
      ) : isError ? (
        <ErrorMessage message="Kon de assets niet ophalen." />
      ) : (
        <VDataTable
          columns={columns}
          rows={filtered}
          getRowKey={(a) => a.id}
          onRowClick={(a) => navigate(`/assets/${a.id}`)}
          emptyState={
            <EmptyState title="Geen assets gevonden" description="Pas je filters aan." />
          }
        />
      )}
    </div>
  );
}
